import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Heart, ArrowDown } from "lucide-react";
import { LINKS, IMAGES } from "@/data/site";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.3 + i * 0.15 },
  }),
};

const HEADLINE = ["Every rescue", "deserves a", "second chance."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      className="relative min-h-[100svh] flex items-end overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.hero}
          alt="Rescue dog in golden light"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2B2D42]/85 via-[#2B2D42]/25 to-transparent" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full pb-20 lg:pb-28"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-xs tracking-[0.3em] uppercase font-medium text-white mb-6"
          data-testid="hero-eyebrow"
        >
          A youth-led animal rescue nonprofit
        </motion.p>

        <h1 className="font-display font-bold text-white text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-tight max-w-4xl">
          {HEADLINE.map((l, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {l}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-8 max-w-xl text-lg text-white/90 leading-relaxed"
        >
          We protect, care for, and support shelter animals to give them the
          second chance they deserve. Join us in creating a kinder world — one
          rescue at a time.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a
            href={LINKS.gofundme}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-donate-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#E07A5F] px-8 py-4 font-semibold text-white shadow-[0_10px_40px_rgba(224,122,95,0.4)] transition-all hover:bg-[#D06A4F] active:scale-95"
          >
            <Heart className="h-5 w-5 fill-white" /> Donate on GoFundMe
          </a>
          <a
            href="#involved"
            data-testid="hero-involved-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 bg-white/10 backdrop-blur-md px-8 py-4 font-semibold text-white transition-all hover:bg-white/20 active:scale-95"
          >
            Get Involved
          </a>
        </motion.div>
      </motion.div>

      <motion.a
        href="#about"
        style={{ opacity: fade }}
        className="absolute bottom-8 right-8 z-10 hidden lg:flex items-center gap-2 text-white/70 text-xs tracking-widest uppercase"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        Scroll <ArrowDown className="h-4 w-4" />
      </motion.a>
    </section>
  );
};
